import { useState } from "react";
import { useSegments, createSegment, type Segment } from "@/hooks/use-segments";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { ListFilter } from "lucide-react";

/** Email lists & segments page — manage contact lists and dynamic segments. */
export default function ListsPage() {
  const { segments, isLoading, error, refetch } = useSegments();
  const [showCreate, setShowCreate] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"all" | "dynamic" | "static">("all");

  const filtered = typeFilter === "all"
    ? segments
    : segments.filter((s) => s.type === typeFilter);

  return (
    <div className="density-gap space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Lists & Segments</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + New List
        </button>
      </div>

      {/* Type filter */}
      <div className="flex gap-1 rounded-lg border border-border p-1">
        {(["all", "static", "dynamic"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={cn(
              "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              typeFilter === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            {t === "all" ? "All" : t === "static" ? "Static Lists" : "Dynamic Segments"}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error} <button onClick={refetch} className="underline">Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ListFilter className="h-8 w-8" />}
          title="No lists yet"
          description="Create a contact list or dynamic segment to organize your audience."
          actionLabel="Create list"
          onAction={() => setShowCreate(true)}
        />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border" data-reveal>
          {filtered.map((segment) => (
            <SegmentRow key={segment.id} segment={segment} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateSegmentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refetch(); }}
        />
      )}
    </div>
  );
}

function SegmentRow({ segment }: { segment: Segment }) {
  return (
    <div className="stagger-item flex items-center justify-between px-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">{segment.name}</p>
        <div className="flex gap-2 text-xs text-muted-foreground">
          <span className={cn(
            "rounded-full px-2 py-0.5",
            segment.type === "dynamic" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
          )}>
            {segment.type}
          </span>
          {segment.cachedCount != null && (
            <span>{segment.cachedCount} contacts</span>
          )}
          {segment.description && <span>— {segment.description}</span>}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">
        {new Date(segment.updatedAt).toLocaleDateString()}
      </span>
    </div>
  );
}

function CreateSegmentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"static" | "dynamic">("static");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await createSegment({ name: name.trim(), description: description.trim() || undefined, type });
      onCreated();
    } catch {
      // stays open on error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New List</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground">✕</button>
        </div>
        <div className="space-y-3">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="List name *" autoFocus className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={2} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          <div className="flex gap-3">
            <label className={cn("flex flex-1 cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-sm", type === "static" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>
              <input type="radio" name="type" value="static" checked={type === "static"} onChange={() => setType("static")} className="sr-only" />
              Static List
            </label>
            <label className={cn("flex flex-1 cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-sm", type === "dynamic" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>
              <input type="radio" name="type" value="dynamic" checked={type === "dynamic"} onChange={() => setType("dynamic")} className="sr-only" />
              Dynamic Segment
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={!name.trim() || isSubmitting} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
